export interface ProfileCompletionItem {
  key: string;
  label: string;
  ok: boolean;
}

export interface ProfileCompletion {
  percentage: number;
  completedCount: number;
  totalCount: number;
  missing: Array<{ key: string; label: string }>;
  items: ProfileCompletionItem[];
}

function truthyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/**
 * Compute a simple profile completion score and checklist
 */
export function computeProfileCompletion(profile: any): ProfileCompletion {
  const items: ProfileCompletionItem[] = [
    {
      key: 'name',
      label: 'Naam',
      ok: truthyString(profile?.displayName) || (truthyString(profile?.firstName) && truthyString(profile?.lastName))
    },
    { key: 'bio', label: 'Bio', ok: truthyString(profile?.bio) },
    { key: 'company', label: 'Bedrijf', ok: truthyString(profile?.company) },
    { key: 'website', label: 'Website', ok: truthyString(profile?.website) },
    { key: 'location', label: 'Locatie', ok: truthyString(profile?.location) || truthyString(profile?.city) },
    { key: 'phone', label: 'Telefoon', ok: truthyString(profile?.phone) || truthyString(profile?.phoneNumber) },
    { key: 'linkedin', label: 'LinkedIn', ok: truthyString(profile?.linkedin) },
    { key: 'expertise', label: 'Expertise', ok: hasArray(profile?.expertise) },
    { key: 'interests', label: 'Interesses', ok: hasArray(profile?.interests) },
    { key: 'languages', label: 'Talen', ok: hasArray(profile?.languages) },
    {
      key: 'availability',
      label: 'Beschikbaarheid',
      ok: !!(
        profile?.availability &&
        (hasArray(profile?.availability?.days) || hasArray(profile?.availability?.timeBlocks))
      )
    }
  ];

  const totalCount = items.length;
  const completedCount = items.filter((i) => i.ok).length;
  const percentage = Math.round((completedCount / totalCount) * 100);
  const missing = items.filter((i) => !i.ok).map((i) => ({ key: i.key, label: i.label }));

  return { percentage, completedCount, totalCount, missing, items };
}

/**
 * Check if user has completed all required onboarding fields
 * This is stricter than profile completion - it checks only essential fields
 * Also checks in onboardingData object if fields are nested there
 */
export function hasCompletedRequiredOnboardingFields(profile: any): {
  isComplete: boolean;
  missingFields: string[];
} {
  const missingFields: string[] = [];
  
  // Helper to get value from profile or onboardingData
  const getValue = (key: string): any => {
    // Check direct on profile first
    if (profile?.[key] !== undefined) return profile[key];
    // Then check in onboardingData object
    if (profile?.onboardingData?.[key] !== undefined) return profile.onboardingData[key];
    return undefined;
  };
  
  // Check name (displayName OR firstName + lastName)
  const displayName = getValue('displayName');
  const firstName = getValue('firstName');
  const lastName = getValue('lastName');
  const hasName = truthyString(displayName) || 
                  (truthyString(firstName) && truthyString(lastName));
  if (!hasName) {
    missingFields.push('Naam');
  }
  
  // Check email
  const email = getValue('email') || profile?.email; // Email might be from auth
  if (!truthyString(email)) {
    missingFields.push('Email');
  }
  
  // Check phone - check multiple possible locations and formats
  // First try getValue (which checks profile[key] and profile.onboardingData[key])
  let phone = getValue('phone') || getValue('phoneNumber');
  
  // If not found, check directly on profile (in case onboardingData structure is different)
  if (!truthyString(phone)) {
    phone = profile?.phone || profile?.phoneNumber;
  }
  
  // Also check nested onboardingData directly
  if (!truthyString(phone)) {
    phone = profile?.onboardingData?.phone || profile?.onboardingData?.phoneNumber;
  }
  
  // Also check if it's stored with dot notation (Firestore nested fields)
  if (!truthyString(phone)) {
    // Check for nested structure like onboardingData.phoneNumber
    const onboardingData = profile?.onboardingData;
    if (onboardingData && typeof onboardingData === 'object') {
      phone = onboardingData.phone || onboardingData.phoneNumber;
    }
  }
  
  if (!truthyString(phone)) {
    console.warn('⚠️ Phone number not found. Profile structure:', {
      hasPhone: !!profile?.phone,
      hasPhoneNumber: !!profile?.phoneNumber,
      hasOnboardingData: !!profile?.onboardingData,
      onboardingDataKeys: profile?.onboardingData ? Object.keys(profile.onboardingData) : [],
      allProfileKeys: Object.keys(profile || {})
    });
    missingFields.push('Telefoonnummer');
  } else {
    console.log('✅ Phone number found:', phone, 'in profile:', {
      'profile.phone': profile?.phone,
      'profile.phoneNumber': profile?.phoneNumber,
      'profile.onboardingData.phone': profile?.onboardingData?.phone,
      'profile.onboardingData.phoneNumber': profile?.onboardingData?.phoneNumber
    });
  }
  
  // Check company
  const company = getValue('company');
  if (!truthyString(company)) {
    missingFields.push('Bedrijfsnaam');
  }
  
  // Check company location (postcode/city)
  const companyLocation = getValue('companyLocation');
  const location = getValue('location');
  const postcode = getValue('postcode');
  const city = getValue('city');
  if (!truthyString(companyLocation) && 
      !truthyString(location) && 
      !truthyString(postcode) &&
      !truthyString(city)) {
    missingFields.push('Postcode/Locatie');
  }
  
  // Check work scope
  const workScope = getValue('workScope');
  if (!workScope) {
    missingFields.push('Werkgebied');
  }
  
  // Check region
  const region = getValue('region');
  if (!truthyString(region)) {
    missingFields.push('Regio');
  }
  
  // Check expertise (at least 1)
  const expertise = getValue('expertise') || profile?.expertise;
  if (!hasArray(expertise)) {
    missingFields.push('Expertise (minimaal 1)');
  }
  
  // Check interests (at least 1)
  const interests = getValue('interests') || profile?.interests;
  if (!hasArray(interests)) {
    missingFields.push('Interesses (minimaal 1)');
  }
  
  return {
    isComplete: missingFields.length === 0,
    missingFields
  };
}


